import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdventureCard } from '../../components/lobby';
import { LoadingSpinner, Toast, Button } from '../../components/ui';
import { AuthPageLayout, AuthForm } from '../../components/layout';
import { pageStyles } from '../../config/theme';
import useGameStore from '../../stores/gameStore';
import { useAuth } from '../../hooks/useAuth';

const AdventureSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { 
    availableAdventures, 
    adventureIds,
    selectedAdventureId,
    isLoadingAdventures,
    adventureError,
    loadAdventures, 
    selectAdventure,
    createSession,
    clearAdventureError,
    clearSessionState
  } = useGameStore();
  
  const [isCreating, setIsCreating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  useEffect(() => {
    loadAdventures();
  }, [loadAdventures]);
  
  const handleCreateSession = async (adventureId: string) => {
    // Check authentication status
    if (!isAuthenticated) {
      setToastMessage('You must be logged in to create a session. Please log in first.');
      setShowToast(true);
      return;
    }

    setIsCreating(true);
    try {
      const { sessionId, playerId } = await createSession(adventureId);
      // Navigate directly to the scene with both sessionId and playerId
      if (playerId) {
        navigate(`/scene/${sessionId}/${playerId}`);
      } else {
        // Fallback to sessionId only if no player ID available
        navigate(`/scene/${sessionId}`);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create session. Please try again.';
      setToastMessage(errorMessage);
      setShowToast(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAdventureSelect = (adventureId: string) => {
    selectAdventure(adventureId);
  };

  const handleRetry = () => {
    clearAdventureError();
    loadAdventures();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission handled by button clicks
  };
  
  return (
    <AuthPageLayout
      title="Select Adventure"
      subtitle={`Choose an adventure to start a new game session${user ? ` (Logged in as ${user.username})` : ''}`}
    >
      <AuthForm onSubmit={handleSubmit}>
        {isLoadingAdventures ? (
          <div className="flex flex-col items-center py-8">
            <LoadingSpinner size="lg" />
            <p style={pageStyles.subtitle} className="mt-4">Loading adventures...</p>
          </div>
        ) : adventureError ? (
          <div className="text-center py-8">
            <div style={pageStyles.error} className="mb-4">
              <p className="text-lg font-medium">Failed to load adventures</p>
              <p className="text-sm">{adventureError}</p>
            </div>
            <Button onClick={handleRetry} variant="glass-primary" fullWidth>
              Try Again
            </Button>
          </div>
        ) : availableAdventures.length === 0 ? (
          <div className="text-center py-8">
            <p style={pageStyles.subtitle} className="text-lg">No adventures available</p>
            <p style={pageStyles.subtitle} className="text-sm mt-2">Contact an administrator to load adventures</p>
          </div>
        ) : (
          <div>
            {availableAdventures.map((adventure, index) => {
              const adventureId = adventureIds[index];
              if (!adventureId) return null;
              
              return (
                <AdventureCard
                  key={adventureId}
                  adventure={adventure}
                  adventureId={adventureId}
                  onSelect={handleAdventureSelect}
                  onCreateSession={handleCreateSession}
                  isSelected={selectedAdventureId === adventureId}
                  isCreating={isCreating && selectedAdventureId === adventureId}
                  style={index > 0 ? { marginTop: '16px' } : {}}
                />
              );
            })}
          </div>
        )}
        
        {showToast && (
          <Toast
            id="adventure-error-toast"
            message={toastMessage}
            type="error"
            onClose={() => setShowToast(false)}
          />
        )}
        
        {/* Return to Home button */}
        <div className="mt-6">
          <span 
            onClick={() => {
              clearSessionState();
              navigate('/');
            }} 
            style={pageStyles.link.primary}
            className="text-sm underline transition-colors duration-200 block cursor-pointer"
          >
            Main Menu
          </span>
        </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default AdventureSelectionPage;
